import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Grid2,
    Button,
    ButtonBase,
    Popover,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    alpha,
} from '@mui/material';
import {
    ArrowDropDown,
    Cancel,
    ChevronLeft,
    ChevronRight,
    Close,
    Delete,
    EventSeat,
    Group,
    MoreVert,
    Add,
    Remove,
    StickyNote2,
    Straighten,
    BeachAccess,
    VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCancellationReason } from '../helpers/cancellationReason';
import { slotOrderIndex, resolveSlotLabel } from '../helpers/slotTemplate';
import {
    formatTimeInVenueTz,
    getDateAtNoonInTimezone,
    getDateKeyInTimezone,
} from '../helpers/formatDateTime';

const AVAILABILITY_THRESHOLD = 20;

const LEGEND_ITEMS = [
    { color: 'success.light', i18nKey: 'timeslots.available' },
    { color: 'warning.light', i18nKey: 'timeslots.limited' },
    { color: 'error.light', i18nKey: 'timeslots.soldOut' },
];

function OverlayIconButton({ side, icon, onClick, disabled }) {
    return (
        <IconButton
            size="small"
            disabled={disabled}
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            sx={{
                position: 'absolute',
                top: '50%',
                [side]: 6,
                transform: 'translateY(-50%)',
                zIndex: 1,
                p: 0,
                color: 'primary.main',
            }}
        >
            {icon}
        </IconButton>
    );
}

function CapacityInput({ value, min, max, onCommit }) {
    const [temp, setTemp] = React.useState(String(value));

    React.useEffect(() => {
        setTemp(String(value));
    }, [value]);

    return (
        <input
            type="number"
            value={temp}
            onChange={(e) => setTemp(e.target.value)}
            onBlur={() => {
                const v = parseInt(temp, 10);
                const clamped = isNaN(v) ? min : Math.min(max, Math.max(min, v));
                onCommit(clamped);
                setTemp(String(clamped));
            }}
            style={{
                width: 52,
                textAlign: 'center',
                border: '1px solid #bbb',
                borderRadius: 4,
                fontSize: 13,
                padding: '3px 4px',
                background: 'transparent',
            }}
        />
    );
}

function InlineEditor({
    timeslot,
    mode,
    values,
    ticketTypes,
    onValuesChange,
    onSave,
    onCancel,
    saving,
    t,
    localeStr,
}) {
    const timeLabel = formatTimeInVenueTz(timeslot.startTime, localeStr, localeStr === 'en-US');
    // Local state for note text so the TextField is not driven by parent state on every
    // keystroke — avoids cursor-reset and re-render jitter. Parent is still updated each
    // change so the dirty-check keeps working.
    const [noteText, setNoteText] = React.useState(values.note || '');

    return (
        <Box sx={{ p: 0.75 }}>
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: 'primary.main',
                    fontSize: '0.8rem',
                    letterSpacing: '0.06em',
                    display: 'block',
                    mb: 0.75,
                }}
            >
                {mode === 'capacity'
                    ? t('timeslots.inlineEdit.editCapacity')
                    : t('timeslots.inlineEdit.editNote')}{' '}
                · {timeLabel}
            </Typography>

            {mode === 'capacity' ? (
                <Box>
                    {(timeslot.ticketTypeBlocks || []).map((block) => {
                        const tt = (ticketTypes || []).find((ty) => ty.id === block.ticketTypeId);
                        const blockCount = tt ? (tt.blockLimit ?? tt.seats?.length ?? 0) : 0;
                        const booked = block.booked ?? 0;
                        const capacity =
                            values[block.ticketTypeId] ??
                            booked + (parseInt(block.available, 10) || 0);
                        const commit = (v) => onValuesChange({ [block.ticketTypeId]: v });
                        // A tier with no finite block (no blockLimit and no seats) has
                        // no capacity to reserve against, so editing it is a no-op.
                        // Show it as unlimited instead of a misleading stepper.
                        const isUnlimited = blockCount <= 0;
                        return (
                            <Box key={block.ticketTypeId} sx={{ mb: 0.75 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        mb: 0.25,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        noWrap
                                        sx={{ flex: 1, mr: 0.5 }}
                                    >
                                        {block.ticketTypeName}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {booked} {t('timeslots.inlineEdit.booked')}
                                    </Typography>
                                </Box>
                                {isUnlimited ? (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block',
                                            textAlign: 'center',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        {t('timeslots.inlineEdit.unlimited')}
                                    </Typography>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.25,
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            disabled={capacity <= booked}
                                            onClick={() => commit(Math.max(booked, capacity - 1))}
                                            sx={{ p: 0.25 }}
                                        >
                                            <Remove sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        <CapacityInput
                                            value={capacity}
                                            min={booked}
                                            max={blockCount}
                                            onCommit={commit}
                                        />
                                        <IconButton
                                            size="small"
                                            disabled={capacity >= blockCount}
                                            onClick={() =>
                                                commit(Math.min(blockCount, capacity + 1))
                                            }
                                            sx={{ p: 0.25 }}
                                        >
                                            <Add sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder={t('timeslots.inlineEdit.notePlaceholder')}
                    value={noteText}
                    onChange={(e) => {
                        setNoteText(e.target.value);
                        onValuesChange({ note: e.target.value });
                    }}
                    sx={{ '& .MuiInputBase-root': { fontSize: '0.95rem' } }}
                />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={onCancel}
                    sx={{ py: 0.6, px: 2.5, fontSize: '0.95rem' }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={saving}
                    onClick={onSave}
                    sx={{ py: 0.6, px: 2.5, fontSize: '0.95rem' }}
                >
                    {t('common.save')}
                </Button>
            </Box>
        </Box>
    );
}

function TimeslotCalendar({
    disabled,
    timeslots = [],
    selectedTimeslot,
    onTimeslotSelect,
    onSeatAssign,
    onTimeslotDelete,
    pendingTimeslotId,
    disableCancelledSelection = false,
    canInlineEdit = false,
    ticketTypes,
    onInlineEditSave,
    onEditRentalDay,
    slotTemplate = [],
}) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [monthPickerAnchor, setMonthPickerAnchor] = useState(null);
    const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());

    const [inlineEdit, setInlineEdit] = useState(null);
    const [menuState, setMenuState] = useState(null);
    const [pendingSwitchTo, setPendingSwitchTo] = useState(null);
    const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // Which rental slot tab is open per day (dateKey -> slotKey). A rental day
    // shows one slot at a time so the cell keeps a constant height instead of
    // stacking all of them (which would reflow the month grid).
    const [rentalPeriodByDay, setRentalPeriodByDay] = useState({});

    const { t } = useTranslation();
    const localeStr = t('widgetLocale');

    const timeslotsByDate = {};
    timeslots.forEach((timeslot) => {
        const dateKey = getDateKeyInTimezone(timeslot.startTime);
        if (!timeslotsByDate[dateKey]) {
            timeslotsByDate[dateKey] = [];
        }
        timeslotsByDate[dateKey].push(timeslot);
    });
    // Within a rental day, order timeslots by their slot's template position;
    // standard days keep start-time order (both slotKeys null -> falls through
    // to time).
    Object.values(timeslotsByDate).forEach((dayTimeslots) => {
        dayTimeslots.sort((a, b) => {
            if (a.slotKey != null || b.slotKey != null) {
                return (
                    slotOrderIndex(slotTemplate, a.slotKey) -
                    slotOrderIndex(slotTemplate, b.slotKey)
                );
            }
            return new Date(a.startTime) - new Date(b.startTime);
        });
    });

    const monthsWithTimeslots = new Set(
        Object.keys(timeslotsByDate).map((dateKey) => dateKey.slice(0, 7)),
    );

    const navigateMonth = (direction) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(currentMonth.getMonth() + direction);
        setCurrentMonth(newMonth);
    };

    const openMonthPicker = (event) => {
        setPickerYear(currentMonth.getFullYear());
        setMonthPickerAnchor(event.currentTarget);
    };

    const closeMonthPicker = () => setMonthPickerAnchor(null);

    const selectMonth = (monthIndex) => {
        setCurrentMonth(new Date(pickerYear, monthIndex, 1));
        closeMonthPicker();
    };

    const pickerMonths = Array.from({ length: 12 }, (_, monthIndex) => {
        const monthKey = `${pickerYear}-${String(monthIndex + 1).padStart(2, '0')}`;
        return {
            monthIndex,
            label: new Intl.DateTimeFormat(localeStr, {
                month: 'short',
                timeZone: 'UTC',
            }).format(new Date(Date.UTC(2024, monthIndex, 1))),
            hasTimeslots: monthsWithTimeslots.has(monthKey),
            isSelected:
                pickerYear === currentMonth.getFullYear() && monthIndex === currentMonth.getMonth(),
        };
    });

    const { calendarDays, weekdayNames, monthName } = (() => {
        const locale = new Intl.Locale(localeStr);
        const firstDayOfWeek = locale.weekInfo?.firstDay || 0;

        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        const monthName = new Intl.DateTimeFormat(localeStr, {
            month: 'long',
            year: 'numeric',
        }).format(currentMonth);

        const firstOfMonth = getDateAtNoonInTimezone(year, month + 1, 1);
        const firstDayOfMonthWeekday = firstOfMonth.getUTCDay();
        const diff = (firstDayOfMonthWeekday - firstDayOfWeek + 7) % 7;

        const weekdayNames = Array.from({ length: 7 }, (_, i) => {
            const weekday = (firstDayOfWeek + i) % 7;
            const tempDate = new Date(Date.UTC(2023, 0, 1 + weekday));
            return new Intl.DateTimeFormat(localeStr, {
                weekday: 'short',
                timeZone: 'UTC',
            }).format(tempDate);
        });

        const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
        const calendarDays = [];
        for (let week = 0; week < 6; week++) {
            const weekDays = [];
            for (let d = 0; d < 7; d++) {
                const dayOffset = week * 7 + d - diff;
                const date = getDateAtNoonInTimezone(year, month + 1, 1 + dayOffset);
                const dateKey = getDateKeyInTimezone(date);
                weekDays.push({
                    date,
                    dateKey,
                    inCurrentMonth: dateKey.startsWith(monthPrefix),
                    key: `${week}-${d}`,
                });
            }
            calendarDays.push(weekDays);
        }

        return { monthName, calendarDays, weekdayNames };
    })();

    const now = new Date();
    const todayKey = getDateKeyInTimezone(now);

    // Inline edit helpers
    const isDirty =
        inlineEdit &&
        JSON.stringify(inlineEdit.values) !== JSON.stringify(inlineEdit.originalValues);

    const buildInitialValues = (timeslot, mode) => {
        if (mode === 'note') {
            return { note: timeslot.note || '' };
        }
        const values = {};
        (timeslot.ticketTypeBlocks || []).forEach((block) => {
            values[block.ticketTypeId] = (block.booked ?? 0) + (parseInt(block.available, 10) || 0);
        });
        return values;
    };

    const openInlineEdit = (timeslotId, mode) => {
        if (inlineEdit && isDirty) {
            setPendingSwitchTo({ timeslotId, mode });
            setUnsavedDialogOpen(true);
            return;
        }
        const timeslot = timeslots.find((ts) => ts.id === timeslotId);
        if (!timeslot) return;
        const values = buildInitialValues(timeslot, mode);
        setInlineEdit({ timeslotId, mode, values, originalValues: { ...values } });
    };

    const closeInlineEdit = () => setInlineEdit(null);

    const handleMenuOpen = (e, timeslotId) => {
        e.stopPropagation();
        setMenuState({ anchor: e.currentTarget, timeslotId });
    };

    const handleMenuClose = () => setMenuState(null);

    const handleMenuItemClick = (mode) => {
        const { timeslotId } = menuState;
        handleMenuClose();
        if (mode === 'delete') {
            onTimeslotDelete?.(timeslotId);
        } else if (mode === 'editRentalDay') {
            const timeslot = timeslots.find((ts) => ts.id === timeslotId);
            if (timeslot?.timeslotGroupId) {
                onEditRentalDay?.(timeslot.timeslotGroupId);
            }
        } else {
            openInlineEdit(timeslotId, mode);
        }
    };

    const handleDiscard = () => {
        setUnsavedDialogOpen(false);
        const { timeslotId, mode } = pendingSwitchTo;
        setPendingSwitchTo(null);
        const timeslot = timeslots.find((ts) => ts.id === timeslotId);
        if (!timeslot) return;
        const values = buildInitialValues(timeslot, mode);
        setInlineEdit({ timeslotId, mode, values, originalValues: { ...values } });
    };

    const handleKeepEditing = () => {
        setUnsavedDialogOpen(false);
        setPendingSwitchTo(null);
    };

    const handleSave = async () => {
        if (!onInlineEditSave || !inlineEdit) return;
        setSaving(true);
        try {
            const { timeslotId, mode, values } = inlineEdit;
            if (mode === 'note') {
                await onInlineEditSave(timeslotId, {
                    note: values.note || null,
                });
            } else {
                const reservedCapacity = {};
                Object.entries(values).forEach(([id, capacity]) => {
                    const tt = (ticketTypes || []).find((ty) => ty.id === parseInt(id, 10));
                    if (!tt) return;
                    const blockCount = tt.blockLimit ?? tt.seats?.length ?? 0;
                    // Unlimited tiers have no finite block to reserve against; skip
                    // them so the editor never writes a meaningless 0.
                    if (blockCount <= 0) return;
                    reservedCapacity[id] = Math.max(0, blockCount - capacity);
                });
                await onInlineEditSave(timeslotId, {
                    ticketTypeReservedCapacity: reservedCapacity,
                });
            }
            closeInlineEdit();
        } catch {
            // onInlineEditSave surfaces the failure (notifyError) and re-throws;
            // keep the editor open so the unsaved values are not lost. The catch
            // clause is also required for React Compiler to optimize this
            // component - it cannot lower a bare try/finally.
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card variant="outlined">
            <CardContent style={{ opacity: disabled ? 0.5 : 1 }}>
                {/* Calendar Header + Legend Inline */}
                <Box
                    display="flex"
                    alignItems="center"
                    mb={2}
                    sx={{
                        flexWrap: 'wrap',
                        rowGap: 1,
                        columnGap: 2,
                        justifyContent: { xs: 'center', sm: 'space-between' },
                    }}
                >
                    {/* Month Changer (left) */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                            onClick={() => navigateMonth(-1)}
                            size="small"
                            edge="start"
                            aria-label={t('widgetDatePicker.prevMonth')}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <ButtonBase
                            onClick={openMonthPicker}
                            aria-label={t('widgetDatePicker.selectMonth')}
                            sx={{
                                borderRadius: 1,
                                gap: 0.5,
                                minWidth: { xs: 150, sm: 210 },
                                justifyContent: 'center',
                            }}
                        >
                            <Typography variant="h6" noWrap>
                                {monthName}
                            </Typography>
                            <ArrowDropDown fontSize="small" sx={{ color: 'text.secondary' }} />
                        </ButtonBase>
                        <IconButton
                            onClick={() => navigateMonth(1)}
                            size="small"
                            aria-label={t('widgetDatePicker.nextMonth')}
                        >
                            <ChevronRight />
                        </IconButton>
                        <Popover
                            open={Boolean(monthPickerAnchor)}
                            anchorEl={monthPickerAnchor}
                            onClose={closeMonthPicker}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <Box sx={{ p: 1.5, width: 280 }}>
                                {/* Year Stepper */}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setPickerYear(pickerYear - 1)}
                                        aria-label={t('widgetDatePicker.prevYear')}
                                    >
                                        <ChevronLeft />
                                    </IconButton>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {pickerYear}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setPickerYear(pickerYear + 1)}
                                        aria-label={t('widgetDatePicker.nextYear')}
                                    >
                                        <ChevronRight />
                                    </IconButton>
                                </Box>
                                {/* Month Grid */}
                                <Grid2 container spacing={0.5}>
                                    {pickerMonths.map(
                                        ({ monthIndex, label, hasTimeslots, isSelected }) => (
                                            <Grid2 key={monthIndex} size={{ xs: 3 }}>
                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant={isSelected ? 'contained' : 'text'}
                                                    onClick={() => selectMonth(monthIndex)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: hasTimeslots
                                                            ? 'bold'
                                                            : 'normal',
                                                        ...(!isSelected && {
                                                            color: hasTimeslots
                                                                ? 'primary.main'
                                                                : 'text.secondary',
                                                        }),
                                                    }}
                                                >
                                                    {label}
                                                </Button>
                                            </Grid2>
                                        ),
                                    )}
                                </Grid2>
                            </Box>
                        </Popover>
                    </Box>
                    {/* Legend (right) */}
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
                    >
                        {LEGEND_ITEMS.map(({ color, i18nKey }) => (
                            <Box key={i18nKey} display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ width: 12, height: 12, backgroundColor: color }} />
                                <Typography variant="caption" color="text.secondary">
                                    {t(i18nKey, { threshold: AVAILABILITY_THRESHOLD })}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Headers + day cells share one horizontal-scroll region with a min
                    width, so the 7-column month grid scrolls sideways instead of crushing
                    its cell content on narrow screens (the month picker + legend above
                    stay fixed). */}
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 980 }}>
                        {/* Day Headers */}
                        <Grid2 container spacing={0} sx={{ mb: 1 }}>
                            {weekdayNames.map((dayName, index) => (
                                <Grid2 key={index} size={{ xs: 12 / 7 }}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        height={32}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            fontWeight="bold"
                                        >
                                            {dayName}
                                        </Typography>
                                    </Box>
                                </Grid2>
                            ))}
                        </Grid2>

                        {/* Calendar Grid */}
                        <Grid2 container spacing={0.5} sx={{ alignItems: 'stretch' }}>
                            {calendarDays.flat().map((dayInfo) => {
                                if (!dayInfo.inCurrentMonth) {
                                    return <Grid2 key={dayInfo.key} size={{ xs: 12 / 7 }} />;
                                }
                                const { dateKey } = dayInfo;
                                const dayTimeslots = timeslotsByDate[dateKey] || [];
                                const hasTimeslots = dayTimeslots.length > 0;
                                const isPast = dayInfo.date < now;
                                const isToday = todayKey === dateKey;

                                // Rental-day members (grouped periods) collapse into a
                                // tab strip that shows one period at a time; any normal
                                // timeslots on the same day still render in full.
                                const rentalMembers = dayTimeslots.filter(
                                    (ts) => ts.timeslotGroupId != null && ts.slotKey,
                                );
                                const hasRentalTabs = rentalMembers.length > 1;
                                const selectedRentalPeriod =
                                    rentalPeriodByDay[dateKey] ?? rentalMembers[0]?.slotKey;
                                const selectedRentalMember = hasRentalTabs
                                    ? (rentalMembers.find(
                                          (m) => m.slotKey === selectedRentalPeriod,
                                      ) ?? rentalMembers[0])
                                    : null;
                                const visibleTimeslots = hasRentalTabs
                                    ? dayTimeslots.filter(
                                          (ts) =>
                                              !rentalMembers.includes(ts) ||
                                              ts === selectedRentalMember,
                                      )
                                    : dayTimeslots;

                                return (
                                    <Grid2 key={dayInfo.key} size={{ xs: 12 / 7 }}>
                                        <Box
                                            sx={{
                                                minHeight: 90,
                                                height: '100%',
                                                border: '1px solid',
                                                borderColor: hasTimeslots
                                                    ? 'divider'
                                                    : 'transparent',
                                                p: 0.5,
                                                backgroundColor: hasTimeslots
                                                    ? 'background.default'
                                                    : 'action.disabledBackground',
                                                opacity: isPast && !isToday ? 0.5 : 1,
                                                position: 'relative',
                                                borderRadius: 1.5,
                                            }}
                                        >
                                            {/* Day Number */}
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: isToday
                                                        ? 'primary.main'
                                                        : 'transparent',
                                                    color: isToday
                                                        ? 'primary.contrastText'
                                                        : dayInfo.inCurrentMonth
                                                          ? 'text.primary'
                                                          : 'text.secondary',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight={hasTimeslots ? 'bold' : 'normal'}
                                                    sx={{ fontSize: '1rem' }}
                                                >
                                                    {parseInt(dateKey.slice(-2), 10)}
                                                </Typography>
                                            </Box>

                                            {/* Timeslots */}
                                            {hasTimeslots && (
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    gap={0.25}
                                                    mt={0.5}
                                                >
                                                    {hasRentalTabs && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: 0.25,
                                                                mb: 0.25,
                                                            }}
                                                        >
                                                            {rentalMembers.map((member) => {
                                                                const isSelectedTab =
                                                                    member.slotKey ===
                                                                    selectedRentalPeriod;
                                                                return (
                                                                    <Button
                                                                        key={member.id}
                                                                        size="small"
                                                                        variant={
                                                                            isSelectedTab
                                                                                ? 'contained'
                                                                                : 'outlined'
                                                                        }
                                                                        onClick={() =>
                                                                            setRentalPeriodByDay(
                                                                                (prev) => ({
                                                                                    ...prev,
                                                                                    [dateKey]:
                                                                                        member.slotKey,
                                                                                }),
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            flex: '1 1 auto',
                                                                            minWidth: 36,
                                                                            px: 0.5,
                                                                            py: 0.25,
                                                                            fontSize: '0.8rem',
                                                                            fontWeight: 700,
                                                                            lineHeight: 1.4,
                                                                            minHeight: 28,
                                                                        }}
                                                                    >
                                                                        {resolveSlotLabel(
                                                                            slotTemplate,
                                                                            member,
                                                                        )}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    {visibleTimeslots.map((timeslot) => {
                                                        const blocks =
                                                            timeslot.ticketTypeBlocks || [];
                                                        const isPending =
                                                            pendingTimeslotId === timeslot.id;
                                                        const isCancelled = Boolean(
                                                            timeslot.cancellationReason?.trim(),
                                                        );
                                                        const isHidden = Boolean(timeslot.isHidden);
                                                        const cancelledLabel = isCancelled
                                                            ? `${t('timeslots.cancelledBanner')}: ${formatCancellationReason(
                                                                  timeslot.cancellationReason,
                                                              )}`
                                                            : '';
                                                        const isEditing =
                                                            inlineEdit?.timeslotId === timeslot.id;

                                                        return (
                                                            <Box
                                                                key={timeslot.id}
                                                                sx={{
                                                                    position: 'relative',
                                                                    borderRadius: 1,
                                                                    ...(isEditing && {
                                                                        border: '1px solid',
                                                                        borderColor:
                                                                            'primary.light',
                                                                        backgroundColor:
                                                                            'background.paper',
                                                                    }),
                                                                }}
                                                            >
                                                                {isEditing ? (
                                                                    <InlineEditor
                                                                        timeslot={timeslot}
                                                                        mode={inlineEdit.mode}
                                                                        values={inlineEdit.values}
                                                                        ticketTypes={ticketTypes}
                                                                        onValuesChange={(patch) =>
                                                                            setInlineEdit(
                                                                                (prev) => ({
                                                                                    ...prev,
                                                                                    values: {
                                                                                        ...prev.values,
                                                                                        ...patch,
                                                                                    },
                                                                                }),
                                                                            )
                                                                        }
                                                                        onSave={handleSave}
                                                                        onCancel={closeInlineEdit}
                                                                        saving={saving}
                                                                        t={t}
                                                                        localeStr={localeStr}
                                                                    />
                                                                ) : (
                                                                    <Box
                                                                        sx={{
                                                                            opacity: isPending
                                                                                ? 0.5
                                                                                : 1,
                                                                        }}
                                                                    >
                                                                        {!hasRentalTabs &&
                                                                            timeslot.slotKey && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        display:
                                                                                            'block',
                                                                                        textAlign:
                                                                                            'center',
                                                                                        fontWeight: 700,
                                                                                        textTransform:
                                                                                            'uppercase',
                                                                                        color: 'primary.main',
                                                                                        fontSize:
                                                                                            '0.6rem',
                                                                                        letterSpacing:
                                                                                            '0.06em',
                                                                                        mb: 0.25,
                                                                                    }}
                                                                                >
                                                                                    {resolveSlotLabel(
                                                                                        slotTemplate,
                                                                                        timeslot,
                                                                                    )}
                                                                                </Typography>
                                                                            )}
                                                                        <Box
                                                                            sx={{
                                                                                position:
                                                                                    'relative',
                                                                            }}
                                                                        >
                                                                            {onSeatAssign && (
                                                                                <OverlayIconButton
                                                                                    side="left"
                                                                                    disabled={
                                                                                        isPending
                                                                                    }
                                                                                    onClick={() =>
                                                                                        onSeatAssign(
                                                                                            timeslot.id,
                                                                                        )
                                                                                    }
                                                                                    icon={
                                                                                        <EventSeat
                                                                                            sx={{
                                                                                                fontSize:
                                                                                                    '14px',
                                                                                            }}
                                                                                        />
                                                                                    }
                                                                                />
                                                                            )}
                                                                            {canInlineEdit &&
                                                                                !isPending && (
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) =>
                                                                                            handleMenuOpen(
                                                                                                e,
                                                                                                timeslot.id,
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            position:
                                                                                                'absolute',
                                                                                            top: '50%',
                                                                                            right: 2,
                                                                                            transform:
                                                                                                'translateY(-50%)',
                                                                                            zIndex: 2,
                                                                                            p: 0,
                                                                                            width: 18,
                                                                                            height: 18,
                                                                                            color: 'text.secondary',
                                                                                            '&:hover':
                                                                                                {
                                                                                                    color: 'primary.main',
                                                                                                },
                                                                                        }}
                                                                                    >
                                                                                        <MoreVert
                                                                                            sx={{
                                                                                                fontSize: 14,
                                                                                            }}
                                                                                        />
                                                                                    </IconButton>
                                                                                )}
                                                                            {onTimeslotDelete &&
                                                                                !canInlineEdit && (
                                                                                    <OverlayIconButton
                                                                                        side="right"
                                                                                        disabled={
                                                                                            isPending
                                                                                        }
                                                                                        onClick={() =>
                                                                                            onTimeslotDelete(
                                                                                                timeslot.id,
                                                                                            )
                                                                                        }
                                                                                        icon={
                                                                                            <Close
                                                                                                sx={{
                                                                                                    fontSize:
                                                                                                        '14px',
                                                                                                }}
                                                                                            />
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            <Button
                                                                                size="small"
                                                                                variant={
                                                                                    selectedTimeslot ===
                                                                                    timeslot.id
                                                                                        ? 'contained'
                                                                                        : 'outlined'
                                                                                }
                                                                                disabled={
                                                                                    disabled ||
                                                                                    isPending ||
                                                                                    !onTimeslotSelect ||
                                                                                    (disableCancelledSelection &&
                                                                                        isCancelled)
                                                                                }
                                                                                onClick={() =>
                                                                                    onTimeslotSelect?.(
                                                                                        timeslot.id,
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    minWidth:
                                                                                        'unset',
                                                                                    fontSize:
                                                                                        '1rem',
                                                                                    lineHeight: 1,
                                                                                    py: 0.25,
                                                                                    px: 0.75,
                                                                                    width: '100%',
                                                                                    minHeight: 22,
                                                                                    '&.MuiButton-contained':
                                                                                        {
                                                                                            backgroundColor:
                                                                                                'primary.main',
                                                                                            color: 'primary.contrastText',
                                                                                        },
                                                                                    '&.MuiButton-outlined':
                                                                                        {
                                                                                            borderColor:
                                                                                                'primary.main',
                                                                                            color: 'primary.main',
                                                                                            ...(isHidden && {
                                                                                                borderStyle:
                                                                                                    'dashed',
                                                                                            }),
                                                                                        },
                                                                                }}
                                                                            >
                                                                                {isHidden && (
                                                                                    <VisibilityOff
                                                                                        sx={{
                                                                                            fontSize: 14,
                                                                                            mr: 0.5,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                {formatTimeInVenueTz(
                                                                                    timeslot.startTime,
                                                                                    localeStr,
                                                                                    localeStr ===
                                                                                        'en-US',
                                                                                )}
                                                                            </Button>
                                                                        </Box>
                                                                        {isCancelled && (
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems:
                                                                                        'flex-start',
                                                                                    gap: 0.5,
                                                                                    mt: 0.5,
                                                                                    px: 0.75,
                                                                                    py: 0.5,
                                                                                    borderRadius: 1,
                                                                                    backgroundColor:
                                                                                        'error.main',
                                                                                    color: 'error.contrastText',
                                                                                }}
                                                                            >
                                                                                <Cancel
                                                                                    sx={{
                                                                                        fontSize:
                                                                                            '1.1rem',
                                                                                        mt: '1px',
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                />
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        flex: 1,
                                                                                        minWidth: 0,
                                                                                        textAlign:
                                                                                            'left',
                                                                                        fontSize:
                                                                                            '0.85rem',
                                                                                        lineHeight: 1.35,
                                                                                        fontWeight: 600,
                                                                                        overflow:
                                                                                            'hidden',
                                                                                        display:
                                                                                            '-webkit-box',
                                                                                        WebkitLineClamp: 3,
                                                                                        WebkitBoxOrient:
                                                                                            'vertical',
                                                                                    }}
                                                                                >
                                                                                    {cancelledLabel}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        {blocks.length > 0 && (
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    flexDirection:
                                                                                        'column',
                                                                                    gap: 0.5,
                                                                                    mt: 0.5,
                                                                                }}
                                                                            >
                                                                                {blocks.map((b) => {
                                                                                    const available =
                                                                                        parseInt(
                                                                                            b.available,
                                                                                            10,
                                                                                        );
                                                                                    const severity =
                                                                                        available >
                                                                                        AVAILABILITY_THRESHOLD
                                                                                            ? 'success'
                                                                                            : available >
                                                                                                0
                                                                                              ? 'warning'
                                                                                              : 'error';
                                                                                    return (
                                                                                        <Box
                                                                                            key={
                                                                                                b.ticketTypeId
                                                                                            }
                                                                                            sx={(
                                                                                                theme,
                                                                                            ) => ({
                                                                                                display:
                                                                                                    'flex',
                                                                                                flexDirection:
                                                                                                    'column',
                                                                                                alignItems:
                                                                                                    'stretch',
                                                                                                backgroundColor:
                                                                                                    alpha(
                                                                                                        theme
                                                                                                            .palette[
                                                                                                            severity
                                                                                                        ]
                                                                                                            .light,
                                                                                                        0.35,
                                                                                                    ),
                                                                                                borderRadius: 1,
                                                                                                py: 0.25,
                                                                                                px: 0.75,
                                                                                                gap: 0.25,
                                                                                                border: `1px solid ${alpha(
                                                                                                    theme
                                                                                                        .palette[
                                                                                                        severity
                                                                                                    ]
                                                                                                        .dark,
                                                                                                    0.2,
                                                                                                )}`,
                                                                                            })}
                                                                                        >
                                                                                            <Typography
                                                                                                noWrap
                                                                                                sx={{
                                                                                                    fontSize:
                                                                                                        '0.95rem',
                                                                                                    lineHeight: 1.3,
                                                                                                    fontWeight: 700,
                                                                                                    textTransform:
                                                                                                        'uppercase',
                                                                                                    color: `${severity}.dark`,
                                                                                                    textAlign:
                                                                                                        'center',
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    b.ticketTypeName
                                                                                                }
                                                                                            </Typography>
                                                                                            <Box
                                                                                                sx={{
                                                                                                    display:
                                                                                                        'flex',
                                                                                                    flexDirection:
                                                                                                        'row',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                    justifyContent:
                                                                                                        'center',
                                                                                                    gap: 0.25,
                                                                                                }}
                                                                                            >
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        display:
                                                                                                            'flex',
                                                                                                        alignItems:
                                                                                                            'center',
                                                                                                        gap: 0.25,
                                                                                                        color: 'text.primary',
                                                                                                    }}
                                                                                                >
                                                                                                    <Group
                                                                                                        sx={{
                                                                                                            fontSize:
                                                                                                                '1.1rem',
                                                                                                        }}
                                                                                                    />
                                                                                                    <Typography
                                                                                                        component="span"
                                                                                                        sx={{
                                                                                                            fontWeight: 700,
                                                                                                            fontSize:
                                                                                                                '1.25rem',
                                                                                                            lineHeight: 1,
                                                                                                        }}
                                                                                                    >
                                                                                                        {b.booked ??
                                                                                                            0}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Typography
                                                                                                    component="span"
                                                                                                    sx={{
                                                                                                        fontSize:
                                                                                                            '1rem',
                                                                                                        lineHeight: 1,
                                                                                                        color: 'text.secondary',
                                                                                                        mx: 0.25,
                                                                                                    }}
                                                                                                >
                                                                                                    +
                                                                                                </Typography>
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        display:
                                                                                                            'flex',
                                                                                                        alignItems:
                                                                                                            'center',
                                                                                                        gap: 0.25,
                                                                                                        color: `${severity}.dark`,
                                                                                                    }}
                                                                                                >
                                                                                                    <EventSeat
                                                                                                        sx={{
                                                                                                            fontSize:
                                                                                                                '1.1rem',
                                                                                                        }}
                                                                                                    />
                                                                                                    <Typography
                                                                                                        component="span"
                                                                                                        sx={{
                                                                                                            fontWeight: 600,
                                                                                                            fontSize:
                                                                                                                '1.25rem',
                                                                                                            lineHeight: 1,
                                                                                                        }}
                                                                                                    >
                                                                                                        {
                                                                                                            available
                                                                                                        }
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                                <Typography
                                                                                                    component="span"
                                                                                                    sx={{
                                                                                                        fontSize:
                                                                                                            '1rem',
                                                                                                        lineHeight: 1,
                                                                                                        color: 'text.secondary',
                                                                                                        mx: 0.25,
                                                                                                    }}
                                                                                                >
                                                                                                    =
                                                                                                </Typography>
                                                                                                <Typography
                                                                                                    component="span"
                                                                                                    sx={{
                                                                                                        fontWeight: 700,
                                                                                                        fontSize:
                                                                                                            '1.25rem',
                                                                                                        lineHeight: 1,
                                                                                                        color: 'text.primary',
                                                                                                    }}
                                                                                                >
                                                                                                    {(b.booked ??
                                                                                                        0) +
                                                                                                        available}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        </Box>
                                                                                    );
                                                                                })}
                                                                            </Box>
                                                                        )}
                                                                        {timeslot.note && (
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems:
                                                                                        'flex-start',
                                                                                    gap: 0.5,
                                                                                    mt: 0.5,
                                                                                }}
                                                                            >
                                                                                <StickyNote2
                                                                                    sx={{
                                                                                        fontSize:
                                                                                            '1.35rem',
                                                                                        color: 'text.disabled',
                                                                                        mt: '1px',
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                />
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                    sx={{
                                                                                        fontSize:
                                                                                            '1.1rem',
                                                                                        lineHeight: 1.4,
                                                                                        overflow:
                                                                                            'hidden',
                                                                                        display:
                                                                                            '-webkit-box',
                                                                                        WebkitLineClamp: 3,
                                                                                        WebkitBoxOrient:
                                                                                            'vertical',
                                                                                    }}
                                                                                >
                                                                                    {timeslot.note}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid2>
                                );
                            })}
                        </Grid2>
                    </Box>
                </Box>
            </CardContent>

            {/* Timeslot inline-edit menu */}
            <Menu
                anchorEl={menuState?.anchor}
                open={Boolean(menuState)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 170 } } }}
            >
                <MenuItem onClick={() => handleMenuItemClick('capacity')}>
                    <ListItemIcon>
                        <Straighten fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('timeslots.inlineEdit.editCapacity')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleMenuItemClick('note')}>
                    <ListItemIcon>
                        <StickyNote2 fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('timeslots.inlineEdit.editNote')}</ListItemText>
                </MenuItem>
                {onEditRentalDay &&
                    timeslots.find((ts) => ts.id === menuState?.timeslotId)?.timeslotGroupId && (
                        <MenuItem onClick={() => handleMenuItemClick('editRentalDay')}>
                            <ListItemIcon>
                                <BeachAccess fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('timeslots.editRentalDay')}</ListItemText>
                        </MenuItem>
                    )}
                {onTimeslotDelete && [
                    <Divider key="divider" />,
                    <MenuItem
                        key="delete"
                        onClick={() => handleMenuItemClick('delete')}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon sx={{ color: 'error.main' }}>
                            <Delete fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('common.delete')}</ListItemText>
                    </MenuItem>,
                ]}
            </Menu>

            {/* Unsaved changes confirmation */}
            <Dialog open={unsavedDialogOpen} onClose={handleKeepEditing} maxWidth="xs" fullWidth>
                <DialogTitle>{t('timeslots.inlineEdit.unsavedTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('timeslots.inlineEdit.unsavedContent')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleKeepEditing} variant="outlined">
                        {t('timeslots.inlineEdit.keepEditing')}
                    </Button>
                    <Button onClick={handleDiscard} variant="contained" color="warning">
                        {t('timeslots.inlineEdit.discard')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default TimeslotCalendar;
